import {MavenTemplateSettings} from './maven-settings-template';
import {MAVEN_COVERAGE_KINDS, MAVEN_EXISTING_HEDGES, MAVEN_RISK_LEVELS} from '../maven-domain';
import {isOverrideComplete, isOverrideValueSupplied, OVERRIDE_VALUE_KEYS} from '../../copilot-shared/opg-copilot-domain';

// Validate the reusable contract only. Portfolio state, fixed dates/strikes and Close selections
// intentionally do not occur in this model and must never gate saving it.
export function isFiniteMavenNumber(value: any): value is number {
    return typeof value === 'number' && isFinite(value);
}
export function isPositiveMavenNumber(value: any): boolean {
    return isFiniteMavenNumber(value) && value > 0;
}

export function validateMavenTemplateSettings(settings: MavenTemplateSettings): string[] {
    if (!settings) { return ['Provide Reusable Template Settings.']; }
    const problems: string[] = [];
    const dates = settings.dates;
    if (!dates || !isFiniteMavenNumber(dates.startOffsetDays) || dates.startOffsetDays < 0
        || Math.floor(dates.startOffsetDays) !== dates.startOffsetDays
        || !isPositiveMavenNumber(dates.rangeDays) || Math.floor(dates.rangeDays) !== dates.rangeDays) {
        problems.push('Template Dates Need a Nonnegative Start Offset and a Positive Range in Whole Days.');
    }
    const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    if (!dates || !Array.isArray(dates.weekdayExclusions)
        || dates.weekdayExclusions.some(day => weekdays.indexOf(day) < 0)
        || weekdays.every(day => dates.weekdayExclusions.indexOf(day as any) >= 0)) {
        problems.push('Template Weekday Exclusions Must Leave at Least One Trading Weekday.');
    }
    if (MAVEN_EXISTING_HEDGES.indexOf(settings.existingHedges) < 0) {
        problems.push('Choose Close or Ignore for Existing Hedges.');
    }
    const callsOn = settings.calls && settings.calls.enabled === true;
    const putsOn = settings.puts && settings.puts.enabled === true;
    if (!callsOn && !putsOn) { problems.push('Enable Calls, Puts, or Both for the Template.'); }
    (['calls', 'puts'] as const).forEach(which => {
        const side = settings[which];
        const label = which === 'calls' ? 'Calls' : 'Puts';
        if (!side || typeof side.enabled !== 'boolean') {
            problems.push(label + ': Choose Whether This Side Is Enabled.'); return;
        }
        if (!side.enabled) { return; }
        if (side.mode && side.mode !== 'custom' && side.mode !== 'preset') { problems.push(label + ': Unknown Hedge Mode.'); return; }
        if (side.mode === 'preset') {
            if (!side.presetId) { problems.push(label + ': Select a Hedging Preset.'); }
            return;
        }
        if (!isFiniteMavenNumber(side.offsetFromShorts)) { problems.push(label + ': Enter a Finite Offset from Shorts.'); }
        if (MAVEN_COVERAGE_KINDS.indexOf(side.coverage) < 0) { problems.push(label + ': Choose a Valid Coverage.'); }
        if (side.coverage === 'range') {
            const mode = side.rangeMode || 'endStrike';
            if (mode !== 'endStrike' && mode !== 'distance') { problems.push(label + ': Choose End Strike or Distance.'); }
            if (mode === 'distance' && !isPositiveMavenNumber(side.rangeSize)) {
                problems.push(label + ': Enter a Positive Finite Distance.');
            }
        }
        if (!side.minProfit || !isPositiveMavenNumber(side.minProfit.amount)
            || typeof side.minProfit.normalized !== 'boolean') {
            problems.push(label + ': Enter a Positive Minimum Profit and Choose Its Amount Mode.');
        }
        const risk = side.maxRisk;
        if (!risk || (risk.kind !== 'relative' && risk.kind !== 'dollar')
            || (risk.kind === 'relative' && MAVEN_RISK_LEVELS.indexOf(risk.level) < 0)
            || (risk.kind === 'dollar' && (!isPositiveMavenNumber(risk.amount) || typeof risk.normalized !== 'boolean'))) {
            problems.push(label + ': Choose a Valid Relative Risk or a Positive Dollar Risk and Its Amount Mode.');
        }
    });
    const indicators = settings.allDatesOverride;
    if (indicators != null) {
        if (!isOverrideComplete(indicators as any, !!callsOn && settings.calls.mode !== 'preset', !!putsOn && settings.puts.mode !== 'preset')) {
            problems.push('All Dates Override: Enter Positive IV and Vertical Skew for Every Enabled Side.');
        }
        if (OVERRIDE_VALUE_KEYS.some(key => indicators[key] != null && !isOverrideValueSupplied(indicators[key]))) {
            problems.push('All Dates Override: Every Supplied IV, HS and VS Must Be Finite and Greater Than Zero.');
        }
    }
    return problems;
}

export function assertMavenTemplateSettings(settings: MavenTemplateSettings): void {
    const problems = validateMavenTemplateSettings(settings);
    if (problems.length) { throw new Error(problems.join(' ')); }
}
